import { navigate } from "gatsby-link"
import React, { Fragment, useEffect, useMemo, useState } from "react"
import { Helmet } from "react-helmet"
import { Button, Col, Container, Modal, ModalBody, Row } from "reactstrap"
import { Link, SocialIcons } from ".."
import isoLanguage from "../../data/languages"
import FMMLogoMobile from "../../images/fmm-logo-mobile.svg"
import FMMLogoMobileWhite from "../../images/fmm-logo-mobile-white.svg"
import FMMLogoWhite from "../../images/fmm-logo-white.svg"
// import FMMLogo from "../../images/fmm-logo.svg"
import HamburgerCloseIcon from "../../images/hamburger-close-icon.svg"
import HamburgerIcon from "../../images/hamburger-icon.svg"
import LanguageIconColored from "../../images/language-icon-colored.svg"
import LanguageIcon from "../../images/language-icon.svg"
import "./Header.scss"
import HeaderMenu from "./HeaderMenu"
import HrefLangs from "./HrefLangs"
import { normalizePath } from "../../lib/urls"
import BrandText from "../BrandText/BrandText"

const Header = ({
  lang,
  languages,
  mobileTrigger,
  slug,
  copyrightMessage,
  noAlternates,
}) => {
  const [languageModal, setLanguageModal] = useState(false)
  const [menuModal, setMenuModal] = useState(false)
  const [pagePathArray, setPagePathArray] = useState("")

  useEffect(() => {
    if (typeof window !== "undefined") {
      setPagePathArray(window?.location.pathname?.substring(1).split("/"))
    } else {
      setPagePathArray("/")
    }
  }, [])

  useEffect(() => {
    mobileTrigger(menuModal)
  }, [menuModal, mobileTrigger])

  // Through normalizePath so the switcher lands on the canonical form directly.
  // Building the path by hand produced "/fr/birth-control-options", which the
  // trailing-slash redirect would then bounce to "/fr/birth-control-options/".
  const navigateToSelectedPath = value => {
    const prefix = value === "en" ? `` : `/${value}`
    navigate(normalizePath(`${prefix}/${pagePathArray.join(`/`)}`))
  }

  const updateLanguage = e => {
    const { value } = e.target
    toggleLanguageModal()

    if (pagePathArray[0] === lang) {
      pagePathArray.shift()
      navigateToSelectedPath(value)
    } else {
      navigateToSelectedPath(value)
    }
  }

  // Derived synchronously rather than through state, so the selector renders
  // identically on the server and after hydration instead of popping in.
  const pageLanguages = useMemo(
    () =>
      isoLanguage?.filter(isoLang =>
        languages?.some(
          fmmLang => fmmLang?.node?.fmmCore?.languageCode === isoLang?.code
        )
      ) || [],
    [languages]
  )

  const selectedLanguageName = pageLanguages.find(
    language => language.code === lang
  )?.name

  // The modal lists every language except the one being read, so on
  // single-language content there is nothing to render and the selector would
  // open empty. Hide the trigger instead.
  const hasOtherLanguages = pageLanguages.some(
    language => language.code !== lang
  )

  const toggleLanguageModal = () => setLanguageModal(!languageModal)
  const toggleMenuModal = () => setMenuModal(!menuModal)

  return (
    <>
      <Helmet>
        <meta
          name="facebook-domain-verification"
          content="5ek1kjuz6ciblskgfkjc2uxenp2re3"
        />
      </Helmet>
      <HrefLangs
        languages={languages}
        slug={slug}
        lang={lang}
        noAlternates={noAlternates}
      />

      <header
        id="header"
        className={`${menuModal ? "mobile-menu-active" : ""} ${lang}`}
      >
        <section className="container">
          <section className="row d-flex justify-content-between">
            <Col xs="6" md="3" className="d-flex align-items-center">
              <Link to="/" lang={lang}>
                <picture>
                  <source
                    media="(max-width: 767px)"
                    srcSet={menuModal ? FMMLogoMobile : FMMLogoMobileWhite}
                    height="40px"
                    width="120px"
                  />
                  <img
                    src={FMMLogoWhite}
                    alt="Find My Method"
                    height="60"
                    width="192"
                  />
                </picture>
              </Link>
            </Col>

            <Col xs="6" md="9" className="d-flex d-lg-none align-items-center">
              <section className="ml-auto">
                {hasOtherLanguages && (
                  <button className="icon-button" onClick={toggleLanguageModal}>
                    <img
                      src={!menuModal ? LanguageIcon : LanguageIconColored}
                      height="22px"
                      width="22px"
                      alt=""
                    />
                  </button>
                )}

                <button className="icon-button ml-3" onClick={toggleMenuModal}>
                  <img
                    src={!menuModal ? HamburgerIcon : HamburgerCloseIcon}
                    height="40"
                    width="40"
                    alt=""
                  />
                </button>
              </section>
            </Col>

            {hasOtherLanguages && (
              <div className="nav d-none d-lg-flex">
                <Button
                  onClick={toggleLanguageModal}
                  className="button button--small button--secondary"
                >
                  <img
                    src={LanguageIcon}
                    width="100%"
                    loading="lazy"
                    className="ml-2"
                    alt="Language Selector"
                  />
                  <span className="lang-name">{selectedLanguageName}</span>
                </Button>
              </div>
            )}
          </section>
          <section className="">
            <nav className="nav main-menu d-none d-lg-flex">
              <HeaderMenu
                lang={lang}
                toggleLanguageModal={toggleLanguageModal}
                selectedLanguageName={selectedLanguageName}
              />
            </nav>
          </section>
        </section>
      </header>

      <Modal
        isOpen={languageModal}
        toggle={toggleLanguageModal}
        returnFocusAfterClose={false}
        className="language-selector"
      >
        <ModalBody>
          <Container>
            <section className="language__holder">
              {pageLanguages?.map((language, index) => (
                <Fragment key={index}>
                  {language.code !== lang && (
                    <button
                      onClick={updateLanguage}
                      className="language__item"
                      value={language.code}
                      name={language.name}
                    >
                      {language.name}
                    </button>
                  )}
                </Fragment>
              ))}
            </section>
          </Container>
        </ModalBody>
      </Modal>

      {/* Mobile */}
      <section className={`mobile-menu ${menuModal ? "active" : ""}`}>
        <nav className="menu">
          <HeaderMenu
            lang={lang}
            toggleLanguageModal={toggleLanguageModal}
            selectedLanguageName={selectedLanguageName}
          />
        </nav>

        <section className="mt-auto pb-5">
          <Row className="d-flex align-items-center">
            <section className="col-12 text-center">
              <BrandText />
            </section>

            <section className="col-12">
              <SocialIcons mobileMenuOpen={true} />
            </section>

            <section className="col-12">
              <p className="footer__copyright">
                © {new Date().getFullYear()} {copyrightMessage}
              </p>
            </section>
          </Row>
        </section>
      </section>
    </>
  )
}

export default Header
